import { memo, useMemo } from 'react';

import { type AssetCustomFieldValue, type BoardCustomFieldValue } from '@air/api/types';
import { PencilIcon, Trash2Icon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { customFieldIconMap } from '~/constants/customFieldIconMap';
import { CUSTOM_FIELD_EDIT_BUTTON, CUSTOM_FIELD_SELECT_LABEL } from '~/constants/testIDs';

export type CustomFieldLabelProps = {
  onClick?: () => void;
  canDeleteCustomFields: boolean;
  canEditCustomFields: boolean;
  field: BoardCustomFieldValue | AssetCustomFieldValue;
};

export const CustomFieldLabel = memo(
  ({ onClick, field, canDeleteCustomFields, canEditCustomFields }: CustomFieldLabelProps) => {
    const Icon = customFieldIconMap[field.type.title];
    const label = field.name;
    const iconAndLabel = useMemo(
      () => (
        <div className="flex items-center gap-1 ads:text-metadata text-gray-10">
          <Icon className="size-3" />
          {label}
        </div>
      ),
      [Icon, label],
    );

    return canDeleteCustomFields || canEditCustomFields ? (
      <Button
        appearance="ghost"
        className="group/custom-field-label -ml-2"
        color="gray"
        // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
        data-testid={`${CUSTOM_FIELD_EDIT_BUTTON}-${label}`}
        onClick={onClick}
        suffix={
          canEditCustomFields || canDeleteCustomFields ? (
            <div className="ads:text-icon-default text-gray-10 opacity-0 group-focus-within/custom-field-label:opacity-100 group-hover/custom-field-label:opacity-100">
              {canEditCustomFields ? (
                <PencilIcon size="xs" />
                // biome-ignore lint/style/noNestedTernary: PLA-1724 — pre-existing debt, not introduced by the icon migration
              ) : canDeleteCustomFields ? (
                <Trash2Icon size="xs" />
              ) : undefined}
            </div>
          ) : undefined
        }
        size="small"
      >
        {iconAndLabel}
      </Button>
    ) : (
      // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
      <div className="flex h-6 items-center text-12" data-testid={`${CUSTOM_FIELD_SELECT_LABEL}-${label}`}>
        {iconAndLabel}
      </div>
    );
  },
);

CustomFieldLabel.displayName = 'CustomFieldLabel';
